'use strict';

var React = require('react');
var styled = require('styled-components');
var reactDom = require('react-dom');
var dom = require('../../../helpers/dom.js');
var functions = require('../../../helpers/functions.js');
var keyboard = require('../../../helpers/keyboard.js');
var filterPopin_styles = require('../Filters/filterPopin.styles.js');
var header_styles = require('./header.styles.js');

function _interopDefaultCompat (e) { return e && typeof e === 'object' && 'default' in e ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefaultCompat(React);
var styled__default = /*#__PURE__*/_interopDefaultCompat(styled);

const GrabColContainer = styled__default.default.div `
  position: absolute;
  right: -6px;
  top: 0;
  height: 100%;
  width: 12px;
  display: flex;
  justify-content: center;
  z-index: 1;
  ${({ $enabled }) => $enabled && 'cursor: col-resize;'}
`;
const GrabColStyled = styled__default.default.div `
  border-right-width: 1px;
  border-right-style: solid;
  border-right-color: transparent;
  height: calc(100% + 1px);
`;
const GrabCol = ({ resizeEnabled, resizeHidden, resizeOnMouseDown, }) => {
    if (resizeHidden) {
        return null;
    }
    const enabled = resizeEnabled === undefined || resizeEnabled;
    return (React__default.default.createElement(GrabColContainer, { "$enabled": enabled, onMouseDown: enabled ? resizeOnMouseDown : undefined, onClick: e => e.stopPropagation() },
        React__default.default.createElement(GrabColStyled, null)));
};
const HeaderDefault = React.forwardRef(({ children, resizeEnabled, resizeHidden, resizeOnMouseDown, asDiv = false, ...props }, ref) => {
    if (asDiv) {
        return React__default.default.createElement("div", { ...props }, children);
    }
    return (React__default.default.createElement(header_styles.HeaderStyled, { ref: ref, ...props },
        children,
        React__default.default.createElement(GrabCol, { resizeEnabled: resizeEnabled, resizeHidden: resizeHidden, resizeOnMouseDown: resizeOnMouseDown })));
});
function getElementByXPath(selector, scope = document) {
    const xpathResult = document.evaluate(selector, scope, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
    return xpathResult.snapshotLength > 0 ? xpathResult.snapshotItem(0) : null;
}
const HeaderWithFilter = ({ id, tableId, filter, renderPopin, colKey, defaultOrder, addToQueryParams, removeFromQueryParams, queryParam, children, isPopinOpen, setOpenPopinKey, ...props }) => {
    // We have to check if we actually change the isPopinOpen state here because it's shared among all the header filters and is called on document event handler level, so that we don't close filter popup that's not ours
    const setShouldPopinBeOpen = React.useCallback((shouldPopinBeOpen) => {
        if (shouldPopinBeOpen && !isPopinOpen) {
            setOpenPopinKey(colKey);
        }
        else if (!shouldPopinBeOpen && isPopinOpen) {
            setOpenPopinKey(null);
        }
    }, [colKey, isPopinOpen, setOpenPopinKey]);
    const [isPopinRightSide, setIsPopinRightSide] = React.useState(false);
    const [top, setTop] = React.useState(0);
    const [left, setLeft] = React.useState(0);
    const headerRef = React.useRef(null);
    const popinRef = React.useRef(null);
    const togglePopinOpen = (e) => {
        if (!headerRef.current)
            return;
        if (!isPopinOpen) {
            const modalAncestor = getElementByXPath("./ancestor::div[@role='dialog']", headerRef.current);
            const headerRect = headerRef.current.getBoundingClientRect();
            const shouldPopinBeRightSide = modalAncestor
                ? modalAncestor.offsetLeft + modalAncestor.offsetWidth - headerRect.x < filterPopin_styles.POPIN_MAX_WIDTH_PX
                : (document?.body?.offsetWidth ?? 0) - headerRect.x < filterPopin_styles.POPIN_MAX_WIDTH_PX;
            const topFilterPosition = headerRect.top + headerRect.height - (modalAncestor?.offsetTop || 0);
            const leftFilterPosition = (shouldPopinBeRightSide ? headerRect.right : headerRect.left) - (modalAncestor?.offsetLeft || 0);
            setTop(topFilterPosition);
            setLeft(leftFilterPosition);
            setIsPopinRightSide(shouldPopinBeRightSide);
            setShouldPopinBeOpen(true);
        }
        else if (!dom.isWithinRefElement(e.target, popinRef)) {
            setShouldPopinBeOpen(false);
        }
    };
    React.useEffect(() => {
        const closeFilterIfClickOutside = (e) => {
            if (headerRef?.current?.contains(e.target) ||
                popinRef?.current?.contains(e.target) ||
                dom.isWithinRefElement(e.target, popinRef)) {
                return;
            }
            setShouldPopinBeOpen(false);
        };
        const handleEnterKey = (e) => {
            if (keyboard.isKey('enter')(e)) {
                setShouldPopinBeOpen(false);
            }
        };
        if (popinRef && isPopinOpen && isPopinRightSide) {
            setLeft(left - Number(popinRef.current?.offsetWidth));
        }
        document.addEventListener('mousedown', closeFilterIfClickOutside);
        document.addEventListener('keydown', handleEnterKey);
        return () => {
            document.removeEventListener('mousedown', closeFilterIfClickOutside);
            document.removeEventListener('keydown', handleEnterKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colKey, setShouldPopinBeOpen]);
    const filterPopinElement = React.useMemo(() => {
        const renderPopinProps = {
            id: `filter-${id}`,
            colKey,
            node: popinRef,
            defaultOrder,
            isPopinRightSide,
            $top: top,
            $left: left,
            addToQueryParams,
            removeFromQueryParams,
            queryParam,
        };
        return renderPopin ? renderPopin(renderPopinProps) : React.cloneElement(filter, renderPopinProps);
    }, [
        renderPopin,
        id,
        colKey,
        top,
        left,
        defaultOrder,
        isPopinRightSide,
        addToQueryParams,
        removeFromQueryParams,
        queryParam,
        filter,
    ]);
    const tableContentEl = tableId ? document.getElementById(tableId) : undefined;
    return (React__default.default.createElement(HeaderDefault, { id: id, ref: headerRef, onClick: togglePopinOpen, ...props },
        React__default.default.createElement(header_styles.HeaderContentStyled, null,
            React__default.default.createElement(header_styles.HeaderTitleStyled, null, children),
            React__default.default.createElement(header_styles.IconFilterStyled, { "$isActive": Boolean(!queryParam && defaultOrder) || Boolean(queryParam && queryParam.order !== 'date'), name: "filter-filled", size: "m" })),
        isPopinOpen && tableContentEl && reactDom.createPortal(filterPopinElement, tableContentEl)));
};
const Header = ({ filter, renderPopin, colKey, addToQueryParams = functions.noop, removeFromQueryParams = functions.noop, queryParam, children, isPopinOpen = false, setOpenPopinKey = functions.noop, ...props }) => {
    if (colKey && (filter || renderPopin)) {
        return (React__default.default.createElement(HeaderWithFilter, { ...props, 
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            filter: filter, 
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            renderPopin: renderPopin, colKey: colKey, addToQueryParams: addToQueryParams, removeFromQueryParams: removeFromQueryParams, queryParam: queryParam, isPopinOpen: isPopinOpen, setOpenPopinKey: setOpenPopinKey }, children));
    }
    return (React__default.default.createElement(HeaderDefault, { ...props },
        React__default.default.createElement(header_styles.HeaderContentStyled, null,
            React__default.default.createElement(header_styles.HeaderTitleStyled, null, children))));
};

exports.GrabColContainer = GrabColContainer;
exports.GrabColStyled = GrabColStyled;
exports.Header = Header;
//# sourceMappingURL=header.js.map
